import { useState } from 'react'
import { CirclePause, CirclePlay, Mic, RotateCcw, Square } from 'lucide-react'
import { useRecorder } from '../hooks/useRecorder'

type Props = {
  lessonId: string
  onSaved: (blob: Blob, fileName: string) => Promise<void>
  onTranscribe: (blob: Blob, fileName: string) => Promise<void>
}

const formatTime = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000)
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

export const RecorderPanel = ({ lessonId, onSaved, onTranscribe }: Props) => {
  const recorder = useRecorder(lessonId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [lastFileName, setLastFileName] = useState('')

  const run = async (action: () => Promise<void>) => {
    setError('')
    try {
      await action()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Белгісіз қате')
    }
  }

  const stopAndSave = () =>
    run(async () => {
      setBusy(true)
      const blob = await recorder.stop()
      const extension = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm'
      const fileName = `sabak-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`
      await onSaved(blob, fileName)
      setLastFileName(fileName)
      setBusy(false)
    })

  return (
    <section className="panel recorder-panel">
      <div className="panel-heading">
        <div><p className="eyebrow">ТІКЕЛЕЙ ЖАЗУ</p><h3>Сабақты жоғалтпай жазыңыз</h3></div>
        <span className={`status-pill ${recorder.state}`}>{recorder.state === 'recording' ? 'Жазылып жатыр' : recorder.state === 'paused' ? 'Кідіртілді' : 'Дайын'}</span>
      </div>
      <div className="recorder-visual">
        <div className={`mic-orb ${recorder.state === 'recording' ? 'active' : ''}`}><Mic /></div>
        <strong>{formatTime(recorder.elapsed)}</strong>
        <div className="level-track"><span style={{ width: `${recorder.level}%` }} /></div>
        <small>Аудио әр 5 секунд сайын қауіпсіз draft ретінде сақталады</small>
      </div>
      <div className="button-row">
        {(recorder.state === 'idle' || recorder.state === 'stopped') && (
          <button className="primary" onClick={() => run(recorder.start)} disabled={busy}><CirclePlay size={18} /> Жазуды бастау</button>
        )}
        {recorder.state === 'recording' && (
          <button className="secondary" onClick={recorder.pause}><CirclePause size={18} /> Кідірту</button>
        )}
        {recorder.state === 'paused' && (
          <button className="secondary" onClick={recorder.resume}><CirclePlay size={18} /> Жалғастыру</button>
        )}
        {(recorder.state === 'recording' || recorder.state === 'paused') && (
          <button className="danger" onClick={stopAndSave} disabled={busy}><Square size={17} /> Тоқтату және сақтау</button>
        )}
      </div>
      {recorder.recoveredBlob && (
        <div className="audio-result">
          <audio controls src={URL.createObjectURL(recorder.recoveredBlob)} />
          <div className="button-row">
            <button className="primary" onClick={() => run(() => onTranscribe(recorder.recoveredBlob!, lastFileName || 'recovered.webm'))}>
              Қазір мәтінге айналдыру
            </button>
            <button className="ghost" onClick={recorder.discardDraft}><RotateCcw size={16} /> Draft-ты тазалау</button>
          </div>
        </div>
      )}
      {error && <div className="notice error">{error}</div>}
    </section>
  )
}
